import React, { useState } from "react";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSpinner,
  faDownload,
  faUpload,
} from "@fortawesome/free-solid-svg-icons";
import "./style.scss";
import { useAppContext } from "../../../../../../AppContext";

function ProductsCustom(props) {
  const [braKey, setBraKey] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [dateRangeData, setDateRangeData] = useState({});
  const [loading, setLoading] = useState(false);
  const [pushingDate, setPushingDate] = useState(null);
  const [error, setError] = useState(null);
  const [dataFetched, setDataFetched] = useState(false);
  const { chimeraserv, user } = useAppContext();

  // Flatten product data for CSV export
  const flattenProductData = (product) => {
    return {
      mfz_id: product.mfz_id || "",
      iso_country_code: product.iso_country_code || "",
      item_id:
        product.item_id != null && !isNaN(product.item_id)
          ? parseInt(product.item_id, 10)
          : 0,
      item_name: product.item_name || "",
      category_id:
        product.category_id != null && !isNaN(product.category_id)
          ? parseInt(product.category_id, 10)
          : 0,
    };
  };

  // Generate CSV from the fetched data
  const generateCSV = (products) => {
    if (!products || products.length === 0) {
      return "";
    }
    const headers = [
      "mfz_id",
      "iso_country_code",
      "item_id",
      "item_name",
      "category_id",
    ];
    const rows = products.map((product) => [
      product.mfz_id || "",
      product.iso_country_code || "",
      product.item_id != null &&
      product.item_id !== "" &&
      !isNaN(product.item_id)
        ? parseInt(product.item_id, 10)
        : 0,
      product.item_name || "",
      product.category_id != null &&
      product.category_id !== "" &&
      !isNaN(product.category_id)
        ? parseInt(product.category_id, 10)
        : 0,
    ]);
    const csvContent = [
      headers.join(","),
      ...rows.map((row) =>
        row
          .map((cell) => {
            const cellStr = String(cell);
            return cellStr.includes(",") || cellStr.includes('"')
              ? `"${cellStr.replace(/"/g, '""')}"`
              : cellStr;
          })
          .join(",")
      ),
    ].join("\n");
    return csvContent;
  };

  // Fetch products by date range
  const handleFetchProducts = async (e) => {
    e.preventDefault();
    setError(null);
    setDateRangeData({});
    setDataFetched(false);

    if (!braKey.trim()) {
      setError("Please enter a Brand Key");
      return;
    }

    if (!startDate || !endDate) {
      setError("Please select both start and end dates");
      return;
    }

    if (startDate > endDate) {
      setError("Start date must be before or equal to end date");
      return;
    }

    setLoading(true);

    try {
      const token = user?.data?.usrKey || chimeraserv.authToken;
      const mfzId = chimeraserv.mfzId || "88149";

      let url = `${chimeraserv.urlInsightBackendReading}/api/products-list-by-date-range/${braKey}/${mfzId}`;
      url += `?startDate=${startDate}&endDate=${endDate}`;

      const response = await axios.get(url, {
        headers: {
          Authorization: token,
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      });

      if (response.data?.success && response.data?.data) {
        setDateRangeData(response.data.data);
        setDataFetched(true);
      } else {
        setError("No products data found in response");
      }
    } catch (err) {
      setError(
        err.response?.data?.error ||
          err.message ||
          "Error fetching products by date range"
      );
      console.error("Products fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  // Download CSV file for a specific date
  const handleDownloadCSV = (date, products) => {
    if (!products || products.length === 0) {
      setError(`No data available for ${date}`);
      return;
    }

    try {
      const csvContent = generateCSV(products);

      // Create blob and trigger download
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;

      // Format filename
      const formattedDate = date.replace(/-/g, "");
      const mfzId = chimeraserv.mfzId || "88149";
      link.setAttribute("download", `${mfzId}_products_${formattedDate}.csv`);

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      setError("Error generating CSV: " + err.message);
    }
  };

  // Push data to S3 for a specific date
  const handlePushToS3 = async (date, products) => {
    if (!products || products.length === 0) {
      setError(`No data available to push for ${date}`);
      return;
    }

    setPushingDate(date);
    setError(null);

    try {
      const token = user?.data?.usrKey || chimeraserv.authToken;
      const mfzId = chimeraserv.mfzId || "88149";
      const formattedDate = date.replace(/-/g, "");
      const fileName = `${mfzId}_products_${formattedDate}.csv`;

      // Flatten products for CSV
      const flattenedProducts = products.map((product) =>
        flattenProductData(product)
      );

      const response = await axios.post(
        `${chimeraserv.urlAwsPusher}/upload/products-custom`,
        {
          fileName: fileName,
          data: flattenedProducts,
        },
        {
          headers: {
            Authorization: token,
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        }
      );

      if (response.data && response.data.success) {
        setError(null);
        alert(
          `Successfully pushed products for ${date} to S3.\nS3 URL: ${response.data.url}`
        );
      } else {
        setError(response.data.error || "Failed to push to S3");
      }
    } catch (error) {
      setError(
        error.response?.data?.error ||
          error.message ||
          `Failed to push products for ${date} to S3`
      );
    } finally {
      setPushingDate(null);
    }
  };

  const dateCount = Object.keys(dateRangeData).length;

  return (
    <div className="products-container">
      <div className="products-header">
        <h2>Products Report by Date Range</h2>
        <p>Fetch and download products data for a date range</p>
      </div>

      <form onSubmit={handleFetchProducts} className="products-form">
        <div className="form-group">
          <label htmlFor="braKey">Brand Key *</label>
          <input
            type="text"
            id="braKey"
            value={braKey}
            onChange={(e) => setBraKey(e.target.value)}
            placeholder="Enter Brand Key (e.g., BRA001)"
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="startDate">Start Date *</label>
          <input
            type="date"
            id="startDate"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="endDate">End Date *</label>
          <input
            type="date"
            id="endDate"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            required
          />
        </div>

        <button type="submit" disabled={loading} className="btn-fetch">
          {loading ? (
            <>
              <FontAwesomeIcon icon={faSpinner} spin /> Fetching...
            </>
          ) : (
            "Fetch Products by Date Range"
          )}
        </button>
      </form>

      {error && <div className="alert alert-error">{error}</div>}

      {dataFetched && dateCount === 0 && (
        <div className="alert alert-warning">
          No products data found for the selected date range
        </div>
      )}

      {dataFetched && dateCount > 0 && (
        <>
          <div className="results-header">
            <h3>Products Data by Date ({dateCount} dates)</h3>
          </div>

          <div className="date-list">
            {Object.entries(dateRangeData).map(([date, products]) => {
              const productList = Array.isArray(products)
                ? products
                : products?.data || [];
              const productCount = productList.length;

              return (
                <div key={date} className="date-item">
                  <div className="date-header">
                    <h4>{date}</h4>
                    <span className="product-count">
                      {productCount} product{productCount !== 1 ? "s" : ""}
                    </span>
                  </div>

                  <div className="date-actions">
                    <button
                      onClick={() => handleDownloadCSV(date, productList)}
                      disabled={loading || productCount === 0}
                      className="btn-action btn-download"
                    >
                      <FontAwesomeIcon icon={faDownload} /> Download CSV
                    </button>
                    <button
                      onClick={() => handlePushToS3(date, productList)}
                      disabled={pushingDate !== null || productCount === 0}
                      className="btn-action btn-push"
                      style={{ marginLeft: "10px" }}
                    >
                      {pushingDate === date ? (
                        <>
                          <FontAwesomeIcon icon={faSpinner} spin /> Pushing...
                        </>
                      ) : (
                        <>
                          <FontAwesomeIcon icon={faUpload} /> Push to S3
                        </>
                      )}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

export default ProductsCustom;
